import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faRightFromBracket,
  faChevronDown,
  faBuilding,
  faPalette,
  faUser,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { useCustomerUiStore } from '@/stores/customer-ui.store';
import { customerAuthService } from '@/services/customer-auth.service';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';
import { CustomerThemeToggle } from '@/components/customer-portal/CustomerThemeToggle';
import { CustomerNotificationBell } from '@/components/customer-portal/CustomerNotificationBell';
import { CustomerLocationContext } from '@/components/customer-portal/CustomerLocationContext';

/**
 * Mobile-first header. On phones it is a 56px bar: menu, the customer's
 * company name as the page's context (centred, truncating), bell and avatar.
 * From `lg` it grows to 64px, drops the menu button (the sidebar is fixed)
 * and shows the company context as a chip plus the user's name.
 */
export function CustomerPortalHeader() {
  const user = useCustomerAuthStore((s) => s.user);
  const refreshToken = useCustomerAuthStore((s) => s.refreshToken);
  const clearAuth = useCustomerAuthStore((s) => s.clearAuth);
  const setMobileSidebarOpen = useCustomerUiStore((s) => s.setMobileSidebarOpen);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Customer';
  const initials =
    displayName
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase() || 'C';
  const companyName = user?.customerName ?? 'Customer Portal';

  const handleLogout = async () => {
    try {
      if (refreshToken) await customerAuthService.logout(refreshToken);
    } catch {
      // ignore — still clear local state
    }
    clearAuth();
    navigate(CUSTOMER_PORTAL_ROUTES.LOGIN);
  };

  const closeMenu = (returnFocus: boolean) => {
    setMenuOpen(false);
    if (returnFocus) menuTriggerRef.current?.focus();
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu(true);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-30 flex h-customer-header shrink-0 items-center gap-2 border-b border-customer-border bg-customer-surface/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-customer-surface/85 sm:px-4 lg:h-16 lg:px-6">
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="customer-pressable flex h-11 w-11 shrink-0 items-center justify-center rounded-customer-sm text-customer-text-secondary transition-colors hover:bg-customer-text/[0.06] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring lg:hidden"
        aria-label="Open navigation"
      >
        <FontAwesomeIcon icon={faBars} className="h-4 w-4" />
      </button>

      {/* Company context — the whole point of the header on a phone. */}
      <div className="flex min-w-0 flex-1 items-center lg:flex-none">
        <div
          className="flex min-w-0 items-center gap-2 rounded-customer-sm px-1 py-1 lg:border lg:border-customer-primary/25 lg:bg-customer-primary/[0.06] lg:px-3 lg:py-1.5"
          title={companyName}
        >
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-customer-primary/10 text-customer-primary-text lg:hidden"
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={faBuilding} className="h-3 w-3" />
          </span>
          <span className="hidden h-1.5 w-1.5 shrink-0 rounded-full bg-customer-primary lg:block" />
          <span className="truncate text-sm font-semibold tracking-tight text-customer-text lg:text-xs lg:tracking-wide lg:text-customer-primary-text">
            {companyName}
          </span>
        </div>
        {/* D-130 Phase 6C — which location this session is looking at. A chip
            for LOCATION contacts, a real selector for ORGANIZATION ones; the
            component decides from the scope the server reported. Hidden below
            `sm` so a 320px header keeps company + bell + avatar legible; it is
            repeated in the page controls, where it is reachable at every width. */}
        <div className="ml-2 hidden min-w-0 sm:block">
          <CustomerLocationContext />
        </div>
      </div>

      <div className="hidden flex-1 lg:block" />

      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {/* B6.19 — how the customer learns a Service Report has been published. */}
        <CustomerNotificationBell />
        <div className="hidden lg:block">
          <CustomerThemeToggle />
        </div>

        <div className="relative" ref={menuRef}>
          <button
            ref={menuTriggerRef}
            onClick={() => setMenuOpen((v) => !v)}
            className="customer-pressable flex h-11 items-center gap-2 rounded-customer-sm px-1.5 text-sm text-customer-text transition-colors hover:bg-customer-text/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring sm:px-2"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label={`Account menu for ${displayName}`}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-customer-primary text-xs font-semibold text-customer-action-ink">
              {initials}
            </span>
            <span className="hidden max-w-[140px] truncate font-medium lg:block">
              {displayName}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="hidden h-3 w-3 text-customer-text-secondary sm:block"
            />
          </button>

          {menuOpen && (
            <div
              role="menu"
              aria-label="Account"
              className="customer-modal-in absolute right-0 top-full z-50 mt-1.5 w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-customer-lg border border-customer-border bg-customer-surface shadow-customer-lg"
            >
              <div className="flex items-center gap-3 border-b border-customer-border px-4 py-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-customer-primary text-sm font-semibold text-customer-action-ink">
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-customer-text">{displayName}</p>
                  {user?.email && (
                    <p className="truncate text-xs text-customer-text-secondary">{user.email}</p>
                  )}
                  {user?.username && (
                    <p className="truncate text-xs text-customer-text-secondary">
                      @{user.username}
                    </p>
                  )}
                  {user?.roleName && (
                    <p className="mt-0.5 text-xs font-medium text-customer-primary-text">
                      {user.roleName}
                    </p>
                  )}
                </div>
              </div>
              <div className="border-b border-customer-border px-3 py-3 lg:hidden">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary">
                  Appearance
                </p>
                <CustomerThemeToggle />
              </div>
              <div className="p-1.5">
                {/* User Profile P2 — the contact's own account and password. The
                    page is self-scoped (no id); what is editable is decided by the
                    server's `capabilities`, never here. */}
                <Link
                  role="menuitem"
                  to={CUSTOMER_PORTAL_ROUTES.ACCOUNT}
                  onClick={() => closeMenu(false)}
                  className="customer-pressable flex min-h-11 w-full items-center gap-2.5 rounded-customer-sm px-3 py-2 text-sm font-medium text-customer-text transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  <FontAwesomeIcon icon={faUser} className="h-3.5 w-3.5" />
                  My account
                </Link>
                <Link
                  role="menuitem"
                  to={CUSTOMER_PORTAL_ROUTES.CHANGE_PASSWORD}
                  onClick={() => closeMenu(false)}
                  className="customer-pressable flex min-h-11 w-full items-center gap-2.5 rounded-customer-sm px-3 py-2 text-sm font-medium text-customer-text transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" />
                  Change password
                </Link>
                {/* D-130 Phase 6B — the full Appearance page (six themes +
                    light/dark). Presentation only; carries no permission. */}
                <Link
                  role="menuitem"
                  to={CUSTOMER_PORTAL_ROUTES.APPEARANCE}
                  onClick={() => closeMenu(false)}
                  className="customer-pressable flex min-h-11 w-full items-center gap-2.5 rounded-customer-sm px-3 py-2 text-sm font-medium text-customer-text transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  <FontAwesomeIcon icon={faPalette} className="h-3.5 w-3.5" />
                  Appearance
                </Link>
                <button
                  role="menuitem"
                  onClick={() => {
                    closeMenu(false);
                    void handleLogout();
                  }}
                  className="customer-pressable flex min-h-11 w-full items-center gap-2.5 rounded-customer-sm px-3 py-2 text-sm font-medium text-customer-danger-text transition-colors hover:bg-customer-danger/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  <FontAwesomeIcon icon={faRightFromBracket} className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
